import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { identify } from '@arcgis/core/rest/identify';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import Query from '@arcgis/core/rest/support/Query';
import type {
  QueryFeatureLayerParams,
  IdentifyMapServiceParams,
  HelperFunctionResult,
} from './types';

/**
 * Acceptable shapes for a search boundary: a GeoJSON FeatureCollection (e.g.
 * the `expanded_polygon` slice on a jurisdiction), a single GeoJSON Feature,
 * a raw GeoJSON Polygon / MultiPolygon geometry, or an ArcGIS-style extent
 * (xmin/ymin/xmax/ymax with optional spatialReference) for viewport scoping.
 */
type ExtentLike = {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
  spatialReference?: { wkid?: number; isWebMercator?: boolean };
};

type BoundaryInput =
  | {
      type: 'FeatureCollection';
      features: { geometry?: { type: string; coordinates: unknown } }[];
    }
  | { type: 'Feature'; geometry?: { type: string; coordinates: unknown } }
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] }
  | ExtentLike
  | null
  | undefined;

const isExtentLike = (b: unknown): b is ExtentLike => {
  if (!b || typeof b !== 'object') return false;
  const e = b as Record<string, unknown>;
  return (
    typeof e.xmin === 'number' &&
    typeof e.ymin === 'number' &&
    typeof e.xmax === 'number' &&
    typeof e.ymax === 'number'
  );
};

/**
 * Convert a GeoJSON-style boundary or an ArcGIS extent into an ArcGIS Polygon
 * in WGS84. Returns null when the input is missing or malformed.
 */
const boundaryToPolygon = (boundary: BoundaryInput): Polygon | null => {
  if (!boundary) return null;

  if (isExtentLike(boundary)) {
    const { xmin, ymin, xmax, ymax, spatialReference } = boundary;
    const wkid = spatialReference?.wkid;
    const isWebMercator =
      spatialReference?.isWebMercator === true ||
      wkid === 102100 ||
      wkid === 3857;
    const project = (x: number, y: number): [number, number] => {
      if (!isWebMercator) return [x, y];
      const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
      return [lng, lat];
    };
    const [x0, y0] = project(xmin, ymin);
    const [x1, y1] = project(xmax, ymin);
    const [x2, y2] = project(xmax, ymax);
    const [x3, y3] = project(xmin, ymax);
    try {
      return new Polygon({
        rings: [
          [
            [x0, y0],
            [x1, y1],
            [x2, y2],
            [x3, y3],
            [x0, y0],
          ],
        ],
        spatialReference: { wkid: 4326 },
      });
    } catch {
      return null;
    }
  }

  let geom: { type: string; coordinates: unknown } | undefined;
  if (boundary.type === 'FeatureCollection') {
    geom = boundary.features?.[0]?.geometry;
  } else if (boundary.type === 'Feature') {
    geom = boundary.geometry;
  } else {
    geom = boundary as { type: string; coordinates: unknown };
  }
  if (!geom?.type || !geom.coordinates) return null;

  let rings: number[][][] | null = null;
  if (geom.type === 'Polygon') {
    rings = geom.coordinates as number[][][];
  } else if (geom.type === 'MultiPolygon') {
    rings = (geom.coordinates as number[][][][]).flat();
  }
  if (!rings?.length) return null;

  try {
    return new Polygon({ rings, spatialReference: { wkid: 4326 } });
  } catch {
    return null;
  }
};

interface SerializedGeometry {
  type: 'polygon' | 'point';
  rings?: number[][][];
  centroid: { latitude: number; longitude: number };
}

const extractGeometry = (
  rawGeom: __esri.Geometry | null | undefined,
): SerializedGeometry | undefined => {
  if (!rawGeom) return undefined;
  const geom: __esri.Geometry = rawGeom.spatialReference?.isWebMercator
    ? (webMercatorUtils.webMercatorToGeographic(rawGeom) as __esri.Geometry)
    : rawGeom;

  if (geom.type === 'polygon') {
    const poly = geom as __esri.Polygon;
    return {
      type: 'polygon',
      rings: poly.rings.map(ring => ring.map(([x, y]) => [x, y])),
      centroid: {
        latitude: poly.centroid?.latitude ?? 0,
        longitude: poly.centroid?.longitude ?? 0,
      },
    };
  }
  if (geom.type === 'point') {
    const pt = geom as __esri.Point;
    return {
      type: 'point',
      centroid: { latitude: pt.latitude, longitude: pt.longitude },
    };
  }
  return undefined;
};

const featureLayerCache: Record<string, FeatureLayer> = {};

const resolveFeatureLayer = async (
  layer: unknown,
): Promise<FeatureLayer | null> => {
  const fl = layer as FeatureLayer & { url?: string };

  if (fl.fields?.length) return fl;

  if (typeof fl.load === 'function') {
    try {
      await fl.load();
      if (fl.fields?.length) return fl;
    } catch {
      // fall through
    }
  }

  const url = fl.url as string | undefined;
  if (url) {
    if (featureLayerCache[url]) return featureLayerCache[url];
    try {
      const created = new FeatureLayer({ url });
      await created.load();
      featureLayerCache[url] = created;
      return created;
    } catch {
      // fall through
    }
  }

  return null;
};

/**
 * Resolves the layer and returns ArcGIS `geometryType` (e.g. "polygon", "polyline", "point").
 * Used to skip parcel text search on point-only layers where polygon results are expected.
 */
export const getFeatureLayerGeometryType = async (
  layer: unknown,
): Promise<string | undefined> => {
  const fl = await resolveFeatureLayer(layer);
  return fl?.geometryType;
};

/**
 * Queries features from an ArcGIS feature layer at a specified geographic point.
 *
 * This function creates a spatial query to find features that intersect with the given
 * longitude/latitude coordinates. It extracts feature attributes and metadata,
 * providing a consistent response format for click-to-identify operations.
 *
 * @param {object} layer - The ArcGIS feature layer to query (must have queryFeatures method)
 * @param {number} longitude - The longitude coordinate for the query point
 * @param {number} latitude - The latitude coordinate for the query point
 * @param {string} [fallbackLayerName] - Optional fallback name if layer title cannot be determined
 *
 * @returns {Promise<HelperFunctionResult | null>} Promise that resolves to:
 *   - Object with sourceTitle and attributes if features found
 *   - null if no features found or query fails
 *
 * @example
 * const result = await queryFeatureLayer({
 *   layer: esriFeatureLayer,
 *   longitude: -122.4194,
 *   latitude: 37.7749,
 *   fallbackLayerName: 'San Francisco Parcels'
 * });
 */
export const queryFeatureLayer = async ({
  layer,
  longitude,
  latitude,
  fallbackLayerName,
  bufferMeters,
}: QueryFeatureLayerParams): Promise<HelperFunctionResult | null> => {
  if (
    !layer ||
    typeof layer !== 'object' ||
    !('queryFeatures' in layer) ||
    typeof layer.queryFeatures !== 'function'
  ) {
    return null;
  }

  try {
    const queryOptions: __esri.QueryProperties = {
      geometry: new Point({
        longitude,
        latitude,
        spatialReference: { wkid: 4326 },
      }),
      outFields: ['*'],
      returnGeometry: true,
      outSpatialReference: { wkid: 4326 },
    };

    if (typeof bufferMeters === 'number' && bufferMeters > 0) {
      queryOptions.distance = bufferMeters;
      queryOptions.units = 'meters';
      queryOptions.spatialRelationship = 'intersects';
    }

    const query = new Query(queryOptions);

    const result = await layer.queryFeatures(query);
    if (result?.features?.length) {
      const primaryResult = result.features[0];
      const nameLayer = (primaryResult.layer || layer) as FeatureLayer;
      const sourceTitle =
        nameLayer?.sourceJSON?.name ||
        nameLayer?.title ||
        fallbackLayerName ||
        'Unknown Layer';

      const geometry = extractGeometry(
        primaryResult.geometry as __esri.Geometry | null | undefined,
      );

      return {
        sourceTitle,
        attributes: {
          ...result.features[0].attributes,
          _sourceLayer: sourceTitle,
        },
        ...(geometry !== undefined ? { geometry } : {}),
      } as HelperFunctionResult;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Feature layer query failed:', error);
  }

  return null;
};

/**
 * Spatial proximity query returning multiple results sorted by distance.
 */
export const queryNearbyFeatures = async ({
  layer,
  longitude,
  latitude,
  fallbackLayerName,
  bufferMeters = 500,
  maxResults = 3,
  signal,
  boundary,
}: {
  layer: unknown;
  longitude: number;
  latitude: number;
  fallbackLayerName?: string;
  bufferMeters?: number;
  maxResults?: number;
  signal?: AbortSignal;
  boundary?: BoundaryInput;
}): Promise<(HelperFunctionResult & { distanceM: number })[]> => {
  if (!layer) return [];

  try {
    const fl = await resolveFeatureLayer(layer);
    if (!fl) return [];

    const query = new Query({
      geometry: new Point({
        longitude,
        latitude,
        spatialReference: { wkid: 4326 },
      }),
      distance: bufferMeters,
      units: 'meters',
      spatialRelationship: 'intersects',
      outFields: ['*'],
      returnGeometry: true,
      outSpatialReference: { wkid: 4326 },
    });

    const result = await fl.queryFeatures(query, { signal });
    if (!result?.features?.length) return [];

    const nameLayer = (result.features[0].layer || fl) as FeatureLayer;
    const sourceTitle =
      nameLayer?.sourceJSON?.name ||
      nameLayer?.title ||
      fallbackLayerName ||
      'Unknown Layer';

    const boundaryPolygon = boundaryToPolygon(boundary);

    const toRad = (d: number) => (d * Math.PI) / 180;
    const haversine = (
      lat1: number,
      lng1: number,
      lat2: number,
      lng2: number,
    ) => {
      const R = 6371000;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    return result.features
      .reduce<(HelperFunctionResult & { distanceM: number })[]>(
        (acc, feature) => {
          const geometry = extractGeometry(
            feature.geometry as __esri.Geometry | null | undefined,
          );
          if (geometry) {
            if (boundaryPolygon) {
              const centroidPt = new Point({
                longitude: geometry.centroid.longitude,
                latitude: geometry.centroid.latitude,
                spatialReference: { wkid: 4326 },
              });
              if (!geometryEngine.contains(boundaryPolygon, centroidPt)) {
                return acc;
              }
            }
            const distanceM = haversine(
              latitude,
              longitude,
              geometry.centroid.latitude,
              geometry.centroid.longitude,
            );
            acc.push({
              sourceTitle,
              attributes: { ...feature.attributes, _sourceLayer: sourceTitle },
              geometry,
              distanceM,
            } as HelperFunctionResult & { distanceM: number });
          }
          return acc;
        },
        [],
      )
      .sort((a, b) => a.distanceM - b.distanceM)
      .slice(0, maxResults);
  } catch (error) {
    if (signal?.aborted) return [];
    // eslint-disable-next-line no-console
    console.error('Nearby features query failed:', error);
    return [];
  }
};

/**
 * Fuzzy text search against an ArcGIS feature layer's string fields.
 *
 * WHERE clause shape: each search word must appear in *some* searchable
 * field (OR across fields), and ALL words must hit (AND across words).
 * That way a query like "isha singh" still matches a parcel record where
 * the owner is split across `OWNER_FIRST` / `OWNER_LAST` columns, or
 * stored "Last, first" in a single `NAME` field — neither layout has any
 * single field that contains both substrings in word-typed order.
 *
 * When `mapCenter` is provided, the query over-fetches matches and then
 * re-orders them by haversine distance to the map center, so results closer
 * to what the user is currently viewing surface first. Without `mapCenter`
 * the server's natural row order is preserved.
 */
export const searchFeatureLayer = async ({
  layer,
  searchText,
  fallbackLayerName,
  maxResults = 5,
  signal,
  fieldNamePatterns,
  mapCenter,
  boundary,
}: {
  layer: unknown;
  searchText: string;
  fallbackLayerName?: string;
  maxResults?: number;
  signal?: AbortSignal;
  fieldNamePatterns?: string[];
  mapCenter?: { latitude: number; longitude: number } | null;
  boundary?: BoundaryInput;
}): Promise<HelperFunctionResult[]> => {
  if (!layer || !searchText?.trim()) return [];

  try {
    const fl = await resolveFeatureLayer(layer);
    if (!fl) return [];

    const escaped = searchText.trim().replace(/'/g, "''");

    const allFields = (fl.fields || []) as __esri.Field[];
    // Accept both the JS SDK's normalized `'string'` and the raw REST
    // `'esriFieldTypeString'` — some services come back un-normalized and
    // the strict equality silently drops every string column.
    const allStringFieldNames = allFields
      .filter(
        f =>
          f.type === 'string' || (f.type as string) === 'esriFieldTypeString',
      )
      .map(f => f.name);

    let stringFields = allStringFieldNames;
    if (fieldNamePatterns && fieldNamePatterns.length > 0) {
      stringFields = allStringFieldNames.filter((fieldName: string) => {
        const lower = fieldName.toLowerCase();
        return fieldNamePatterns.some(p => lower.includes(p));
      });
    }

    if (!stringFields.length) {
      // eslint-disable-next-line no-console
      console.warn('[searchFeatureLayer] no searchable string fields', {
        layer: fallbackLayerName,
        searchText,
        allStringFields: allStringFieldNames,
        fieldNamePatterns,
      });
      return [];
    }

    const words = escaped.split(/\s+/).filter(Boolean);

    const where = words
      .map((w: string) => {
        const fieldClauses = stringFields.map(
          (name: string) => `UPPER(${name}) LIKE UPPER('%${w}%')`,
        );
        return fieldClauses.length > 1
          ? `(${fieldClauses.join(' OR ')})`
          : fieldClauses[0];
      })
      .join(' AND ');

    const FETCH_MULTIPLIER = 5;
    const FETCH_CAP = 50;
    const fetchCount = mapCenter
      ? Math.min(FETCH_CAP, Math.max(maxResults, maxResults * FETCH_MULTIPLIER))
      : maxResults;

    const boundaryPolygon = boundaryToPolygon(boundary);

    const query = new Query({
      where,
      outFields: ['*'],
      returnGeometry: true,
      outSpatialReference: { wkid: 4326 },
      num: fetchCount,
      ...(boundaryPolygon
        ? {
            geometry: boundaryPolygon,
            spatialRelationship: 'intersects' as const,
          }
        : {}),
    });

    const result = await fl.queryFeatures(query, { signal });
    if (!result?.features?.length) return [];

    const nameLayer = (result.features[0].layer || fl) as FeatureLayer;
    const sourceTitle =
      nameLayer?.sourceJSON?.name ||
      nameLayer?.title ||
      fallbackLayerName ||
      'Unknown Layer';

    const searchWords = escaped.toLowerCase().split(/\s+/).filter(Boolean);

    const toRad = (d: number) => (d * Math.PI) / 180;
    const haversine = (
      lat1: number,
      lng1: number,
      lat2: number,
      lng2: number,
    ) => {
      const R = 6371000;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const enriched = result.features.reduce<
      (HelperFunctionResult & { _distanceM: number })[]
    >((acc, feature) => {
      const geometry = extractGeometry(
        feature.geometry as __esri.Geometry | null | undefined,
      );
      if (geometry) {
        // Surface the field with the most word hits (any hit beats none),
        // since after the cross-field SQL change a match is no longer
        // guaranteed to land entirely within one column.
        let matchedField = '';
        let matchedValue = '';
        let bestHits = 0;
        for (const fname of stringFields) {
          const val = feature.attributes?.[fname];
          if (typeof val !== 'string' || !val.trim()) continue;
          const valLower = val.toLowerCase();
          const hits = searchWords.reduce(
            (n, w) => n + (valLower.includes(w) ? 1 : 0),
            0,
          );
          if (hits > bestHits) {
            bestHits = hits;
            matchedField = fname;
            matchedValue = val.trim();
            if (hits === searchWords.length) break;
          }
        }

        const distanceM = mapCenter
          ? haversine(
              mapCenter.latitude,
              mapCenter.longitude,
              geometry.centroid.latitude,
              geometry.centroid.longitude,
            )
          : Number.POSITIVE_INFINITY;

        acc.push({
          sourceTitle,
          attributes: {
            ...feature.attributes,
            _sourceLayer: sourceTitle,
            _matchedField: matchedField,
            _matchedValue: matchedValue,
          },
          geometry,
          _distanceM: distanceM,
        } as HelperFunctionResult & { _distanceM: number });
      }
      return acc;
    }, []);

    if (mapCenter) {
      enriched.sort((a, b) => a._distanceM - b._distanceM);
    }

    return enriched.slice(0, maxResults).map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ _distanceM, ...rest }) => rest as HelperFunctionResult,
    );
  } catch (error) {
    if (signal?.aborted) return [];
    // eslint-disable-next-line no-console
    console.error('Feature layer search failed:', error);
    return [];
  }
};

/**
 * Identifies features from an ArcGIS map service at a specified geographic point.
 *
 * This function performs an identify operation on map image services using the ArcGIS
 * REST API. It handles authentication via tokens and provides a consistent response
 * format for click-to-identify operations on map services (as opposed to feature services).
 *
 * @param {string} url - The map service URL to query
 * @param {number} longitude - The longitude coordinate for the identify point
 * @param {number} latitude - The latitude coordinate for the identify point
 * @param {object} view - The map view object containing width, height, and extent information
 * @param {string} [token] - Optional authentication token for secured services
 * @param {string} [fallbackLayerName] - Optional fallback name if layer name cannot be determined
 *
 * @returns {Promise<HelperFunctionResult | null>} Promise that resolves to:
 *   - Object with sourceTitle and attributes if features identified
 *   - null if no features found or identify operation fails
 *
 * @example
 * const result = await identifyMapService({
 *   url: 'https://services.arcgis.com/sample/MapServer',
 *   longitude: -122.4194,
 *   latitude: 37.7749,
 *   view: mapView,
 *   token: 'authentication_token_here',
 *   fallbackLayerName: 'City Boundaries'
 * });
 */
export const identifyMapService = async ({
  url,
  longitude,
  latitude,
  view,
  token,
  fallbackLayerName,
  tolerance,
}: IdentifyMapServiceParams): Promise<HelperFunctionResult | null> => {
  try {
    const identifyParams = new IdentifyParameters({
      tolerance: typeof tolerance === 'number' ? tolerance : 3,
      layerOption: 'top',
      width: view?.width,
      height: view?.height,
      geometry: new Point({
        longitude,
        latitude,
        spatialReference: { wkid: 4326 },
      }),
      mapExtent: view?.extent,
    });

    const authenticatedUrl = token ? `${url}?token=${token}` : url;
    const response = await identify(authenticatedUrl, identifyParams);

    if (response?.results?.length) {
      const sourceTitle =
        response.results[0].layerName ||
        fallbackLayerName ||
        'Unknown Map Service';

      return {
        sourceTitle,
        attributes: {
          ...response.results[0].feature.attributes,
          _sourceLayer: sourceTitle,
        },
      };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Map service identify failed:', error);
  }

  return null;
};
